/* eslint-disable i18next/no-literal-string */

import {flushAction} from './spanCollector'

const SESSION_STORAGE_KEY = 'iam.session-id'

let currentActionId: string | null = null

function generateHexId(bytes: number): string {
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID (e.g. non-secure contexts)
  console.warn('[observability] crypto.randomUUID not available — using Math.random fallback')
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function sessionId(): string {
  let id = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (!id) {
    id = generateUuid()
    sessionStorage.setItem(SESSION_STORAGE_KEY, id)
  }
  return id
}

export function getCurrentActionId(): string | null {
  return currentActionId
}

export function startAction(label: string): () => void {
  const actionId = generateUuid()
  currentActionId = actionId
  const startedAt = new Date()

  performance.mark(`action-start:${label}`)

  let ended = false
  return () => {
    if (ended) return
    ended = true

    performance.mark(`action-end:${label}`)
    let durationMs = 0
    try {
      performance.measure(`action-duration:${label}`, `action-start:${label}`, `action-end:${label}`)
      durationMs = Math.round(performance.now() - (startedAt.getTime() - performance.timeOrigin))
    } catch (measureError) {
      // performance.measure can throw DOMException if marks were already cleared (e.g., performance.clearMarks was called).
      // This is non-fatal — the span timing is lost but the action lifecycle continues correctly.
      console.debug('[observability] performance.measure failed (marks likely cleared):', measureError)
      durationMs = Date.now() - startedAt.getTime()
    }

    if (currentActionId === actionId) {
      currentActionId = null
    }

    // Fire-and-forget flush — never throws, never surfaces errors as UI issues.
    // v1 tab-close: spans in flight on unload are dropped (no sendBeacon in v1 per spec §12.6).
    void flushAction(actionId, label, startedAt, durationMs, sessionId())
  }
}

export function buildTraceparent(): string {
  const actionId = currentActionId
  let traceId: string
  if (actionId) {
    // Derive a stable 32-hex traceId from the action UUID (strip dashes, pad/trim to 32)
    traceId = actionId.replace(/-/g, '').padEnd(32, '0').slice(0, 32)
  } else {
    // No active action — use session id as trace id base
    const sid = sessionId()
    traceId = sid.replace(/-/g, '').padEnd(32, '0').slice(0, 32)
  }
  const spanId = generateHexId(8)
  return `00-${traceId}-${spanId}-01`
}

/** Test-only: reset module state between Vitest runs */
export function _resetForTest(): void {
  currentActionId = null
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}
